import { useRef, useCallback } from 'react';

/**
 * Hook para implementar un circuit breaker
 * @param {number} threshold - Número de llamadas antes de activar el breaker
 * @param {number} timeout - Tiempo en ms para resetear el breaker
 */
export function useCircuitBreaker(threshold = 10, timeout = 30000) {
  const callCount = useRef(0);
  const lastCallTime = useRef(0);
  const isOpen = useRef(false);
  const hasWarned = useRef(false);

  const execute = useCallback((fn, ...args) => {
    const now = Date.now();
    
    // Resetear si ha pasado el timeout
    if (now - lastCallTime.current > timeout) {
      callCount.current = 0;
      isOpen.current = false;
      hasWarned.current = false;
    }

    // Si el circuit breaker está abierto, no ejecutar
    if (isOpen.current) {
      // Solo mostrar el warning una vez, no constantemente
      if (!hasWarned.current) {
        console.warn('Circuit breaker is OPEN - preventing execution');
        hasWarned.current = true;
      }
      return Promise.reject(new Error('Circuit breaker is open'));
    }

    // Incrementar contador
    callCount.current++;
    lastCallTime.current = now;

    // Solo mostrar logs cada 5 llamadas para no saturar
    if (callCount.current % 5 === 0 || callCount.current === threshold) {
      console.log(`Circuit breaker: call #${callCount.current}/${threshold}`);
    }

    // Si alcanzamos el threshold, abrir el circuit breaker
    if (callCount.current >= threshold) {
      isOpen.current = true;
      console.error(`Circuit breaker OPENED after ${threshold} calls`);
      return Promise.reject(new Error('Circuit breaker opened'));
    }

    // Ejecutar la función normalmente
    return fn(...args);
  }, [threshold, timeout]);

  const reset = useCallback(() => {
    callCount.current = 0;
    isOpen.current = false;
    lastCallTime.current = 0;
    console.log('Circuit breaker RESET');
  }, []);

  return { execute, reset, isOpen: isOpen.current, callCount: callCount.current };
} 